import axios from 'axios';
// Removed token utils - using direct localStorage/sessionStorage access

const API_URL = `${process.env.REACT_APP_API_URL}/api`;
// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_URL}/messaging`,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem('token') || sessionStorage.getItem('token');

    if (token) {
      config.headers['x-auth-token'] = token;

      // Debug logging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log('API Request:', {
          url: config.url,
          method: config.method.toUpperCase(),
        });
      }
    } else {
      console.warn('No authentication token found');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Authentication failed:', error.response.data);
    } else if (error.response?.status === 403) {
      console.error('Access denied:', error.response.data);
    } else if (error.response) {
      console.error('API Error:', error.response.data);
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Get all conversations with unread counts
  getConversations: async () => {
    console.log('🌐 API: Making request to /conversations');
    const response = await apiClient.get('/conversations');
    console.log('🌐 API: Response received:', response.data);
    console.log('🌐 API: Response status:', response.status);
    return response.data;
  },

  // Get messages for a conversation
  getMessages: async (conversationId, limit = 50, skip = 0) => {
    const response = await apiClient.get(
      `/conversations/${conversationId}/messages`,
      { params: { limit, skip } }
    );
    return response.data;
  },

  // Send a message over REST — the fallback while the live socket is down.
  // `payload` is the text, or { type, mediaUrl, media } for a photo, video,
  // voice message or GIF.
  sendMessage: async (conversationId, payload) => {
    const body = typeof payload === 'string' ? { text: payload } : payload;
    const response = await apiClient.post(
      `/conversations/${conversationId}/messages`,
      body
    );
    return response.data;
  },

  // GIF search for chat (trending without a query). 503 until it's set up.
  gifsEnabled: async () => {
    const response = await apiClient.get('/gifs/status');
    return !!response.data?.enabled;
  },
  searchGifs: async (q, page = 1) => {
    const response = await apiClient.get('/gifs', { params: { q, page } });
    return response.data;
  },

  // Create new conversation
  createConversation: async (participants) => {
    const response = await apiClient.post('/conversations', {
      participants,
    });
    return response.data;
  },

  // Send manual notification email for a conversation participant
  notifyConversationParticipant: async (
    conversationId,
    recipientUserId,
    { force = false } = {}
  ) => {
    const response = await apiClient.post(
      `/conversations/${conversationId}/notify`,
      {
        recipientUserId,
        force,
      }
    );
    return response.data;
  },

  // Mark conversation as read
  markAsRead: async (conversationId) => {
    const response = await apiClient.post(
      `/conversations/${conversationId}/mark-read`
    );
    return response.data;
  },

  // Get unread count for a conversation
  getUnreadCount: async (conversationId) => {
    const response = await apiClient.get(
      `/conversations/${conversationId}/unread-count`
    );
    return response.data;
  },

  // FCM Token Management
  registerFCMToken: async (token) => {
    const response = await fetch(`${API_URL}/fcm/register-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token':
          localStorage.getItem('token') || sessionStorage.getItem('token'),
      },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  unregisterFCMToken: async (token) => {
    const response = await fetch(`${API_URL}/fcm/unregister-token`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token':
          localStorage.getItem('token') || sessionStorage.getItem('token'),
      },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  // Get notification preferences
  getNotificationPreferences: async () => {
    const response = await fetch(`${API_URL}/fcm/notification-preferences`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token':
          localStorage.getItem('token') || sessionStorage.getItem('token'),
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  // Update notification preferences
  updateNotificationPreferences: async (preferences) => {
    const response = await fetch(`${API_URL}/fcm/notification-preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token':
          localStorage.getItem('token') || sessionStorage.getItem('token'),
      },
      body: JSON.stringify({ preferences }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  // Reset notification preferences to defaults
  resetNotificationPreferences: async () => {
    const response = await fetch(
      `${API_URL}/fcm/notification-preferences/reset`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token':
            localStorage.getItem('token') || sessionStorage.getItem('token'),
        },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },
};
